import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, FileDown, ShieldAlert } from "lucide-react";
import { API, api } from "@/lib/api";
import { useLang } from "@/lib/i18n";

export default function DataRoom() {
  const { token } = useParams();
  const { t, lang } = useLang();
  const dd = (d, f) => (lang === "pt" && d[`${f}_pt`]) ? d[`${f}_pt`] : d[f];
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/secure/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || "This access link is invalid or has expired."));
  }, [token]);

  return (
    <main data-testid="data-room-page" className="min-h-screen bg-ink text-ivory grain">
      <div className="max-w-3xl mx-auto px-6 py-24">
        <span className="font-mono text-[10px] tracking-[0.35em] uppercase text-gold">BSGOTECH</span>
        {error ? (
          <div className="mt-10 border border-red-400/30 bg-red-400/5 p-10 text-center">
            <ShieldAlert size={22} className="text-red-300 mx-auto" />
            <h1 className="font-serif text-3xl mt-5">{t("dataroom.page.unavailable")}</h1>
            <p data-testid="data-room-error" className="mt-3 text-white/50">{error}</p>
            <a href="/" className="inline-block mt-8 px-6 py-3 border border-white/20 font-mono text-[10px] tracking-[0.2em] uppercase hover:border-gold hover:text-gold transition-colors">{t("dataroom.page.back")}</a>
          </div>
        ) : !data ? (
          <p className="mt-16 text-white/40 font-mono text-xs tracking-[0.3em] uppercase">{t("dataroom.page.verify")}</p>
        ) : (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="mt-10 border border-gold/30 bg-surface/60 p-8 lg:p-12 relative overflow-hidden">
              <div className="absolute -top-20 right-0 w-72 h-72 bg-gold/[0.06] blur-[100px] rounded-full pointer-events-none" />
              <div className="flex items-center gap-4 mb-6">
                <span className="w-12 h-12 border border-gold/50 flex items-center justify-center"><Lock size={18} className="text-gold" /></span>
                <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-gold">{t("resources.confidential")}</span>
              </div>
              <h1 className="font-serif text-4xl" data-testid="data-room-title">{data.resource_title}</h1>
              <p className="mt-3 text-white/50 text-sm">
                {t("dataroom.page.valid")} {new Date(data.expires_at).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
              </p>

              <div className="mt-10 divide-y divide-white/10 border-y border-white/10">
                {data.documents.length === 0 && <p className="py-6 text-white/40 text-sm">{t("dataroom.page.empty")}</p>}
                {data.documents.map((d) => (
                  <div key={d.id} data-testid={`data-room-doc-${d.id}`} className="py-5 flex items-center gap-5">
                    <div className="flex-1 min-w-0">
                      {dd(d, "category") && <span className="font-mono text-[9px] tracking-[0.25em] uppercase text-gold">{dd(d, "category")}</span>}
                      <p className="text-ivory font-medium mt-1">{dd(d, "title")}</p>
                      {dd(d, "description") && <p className="text-sm text-white/45 mt-1">{dd(d, "description")}</p>}
                      {d.pages ? <p className="font-mono text-[9px] tracking-[0.2em] uppercase text-white/30 mt-2">{d.pages} {t("resources.pages")} · PDF</p> : null}
                    </div>
                    <a
                      data-testid={`data-room-download-${d.id}`}
                      href={`${API}/secure/${token}/file/${d.id}`}
                      className="shrink-0 inline-flex items-center gap-2 px-5 py-3 bg-gold text-ink font-mono text-[10px] tracking-[0.2em] uppercase hover:bg-gold-light transition-colors"
                    >
                      <FileDown size={13} /> {t("dataroom.page.download")}
                    </a>
                  </div>
                ))}
              </div>
              <p className="mt-8 font-mono text-[10px] tracking-wide text-white/30 leading-relaxed">
                {t("dataroom.page.disclosure")}
              </p>
            </div>
          </motion.div>
        )}
      </div>
    </main>
  );
}
